import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

/**
 * /inventory 하단 자주 묻는 질문(오너 확정 2026-07-17, A안 — 하단 아코디언). 셀러가 CS로
 * 물어볼 법한 개념(입금 완료 차감·예약/가용·소급 없음·이름 매칭)을 페이지 안에서 선제
 * 답변한다(문의 방지). 정적 콘텐츠 dumb 컴포넌트 — props/state 없음(아코디언 열림 상태는
 * radix 내부, 기본 전부 접힘).
 *
 * 죽은 토큰 주의(#126 학습): ui/accordion 기본 클래스의 text-muted-foreground(셰브론)·
 * border-b(항목)는 이 레포 tailwind config에 토큰이 없거나 톤이 안 맞아 사용처에서 명시
 * slate 클래스로 덮는다.
 */
export function InventoryFaq() {
  return (
    <div className="rounded-2xl bg-white px-5 pb-1 pt-4 shadow-sm ring-1 ring-slate-200">
      <h2 className="text-sm font-semibold text-slate-900">자주 묻는 질문</h2>
      <Accordion type="single" collapsible className="mt-1">
        <AccordionItem value="paid-transition" className="border-slate-100">
          <AccordionTrigger className="break-keep py-3 text-slate-700 hover:no-underline hover:text-slate-900 [&>svg]:text-slate-400">
            주문이 입금 완료되면 재고가 어떻게 되나요?
          </AccordionTrigger>
          <AccordionContent className="break-keep text-sm leading-relaxed text-slate-500">
            예약이 실제 차감으로 옮겨가는 것뿐이라, 판매 가능한 수량(가용)은 변하지 않습니다.
            <div className="my-3 overflow-x-auto">
              <table className="w-full max-w-sm text-xs tabular-nums">
                {/* a11y(codexloop 수용): 상태 열 머리글은 sr-only로 제공 + scope 지정 —
                    스크린리더가 숫자 셀을 '입금 전/후' 행과 연결할 수 있게 한다. 행 이름
                    th는 font-normal·text-left로 기존 td 시각 유지. */}
                <thead>
                  <tr className="text-slate-400">
                    <th scope="col" className="py-1 pr-4 text-left font-medium">
                      <span className="sr-only">상태</span>
                    </th>
                    <th scope="col" className="px-3 py-1 text-right font-medium">현재고</th>
                    <th scope="col" className="px-3 py-1 text-right font-medium">예약</th>
                    <th scope="col" className="py-1 pl-3 text-right font-medium">가용</th>
                  </tr>
                </thead>
                <tbody className="text-slate-600">
                  <tr className="border-t border-slate-100">
                    <th scope="row" className="whitespace-nowrap py-1.5 pr-4 text-left font-normal">
                      입금 전 (2개 예약)
                    </th>
                    <td className="px-3 py-1.5 text-right">10</td>
                    <td className="px-3 py-1.5 text-right">2</td>
                    <td className="py-1.5 pl-3 text-right">8</td>
                  </tr>
                  <tr className="border-t border-slate-100">
                    <th scope="row" className="whitespace-nowrap py-1.5 pr-4 text-left font-normal">
                      입금 완료 후
                    </th>
                    <td className="px-3 py-1.5 text-right">8</td>
                    <td className="px-3 py-1.5 text-right">0</td>
                    <td className="py-1.5 pl-3 text-right font-semibold text-slate-900">8</td>
                  </tr>
                </tbody>
              </table>
            </div>
            입금 전으로 되돌리면 차감분이 그대로 복원됩니다.
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="reserved-available" className="border-slate-100">
          <AccordionTrigger className="break-keep py-3 text-slate-700 hover:no-underline hover:text-slate-900 [&>svg]:text-slate-400">
            예약·가용이 무엇인가요?
          </AccordionTrigger>
          <AccordionContent className="break-keep text-sm leading-relaxed text-slate-500">
            {/* '예약'을 셀러가 아는 주문 상태 어휘로 연결(오너 2026-07-17) — 집계 predicate는
                status NOT IN ('입금 완료','출고 완료') = 미지정·부분 입금. 문장별 줄바꿈. */}
            예약은 아직 입금 전 상태(미지정·부분 입금)인 주문의 수량입니다 (최근 90일~미래
            30일 기준).
            <br />
            가용은 현재고에서 예약을 뺀, 지금 팔 수 있는 수량입니다 (가용 = 현재고 − 예약).
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="no-retroactive" className="border-slate-100">
          <AccordionTrigger className="break-keep py-3 text-slate-700 hover:no-underline hover:text-slate-900 [&>svg]:text-slate-400">
            이미 입금 완료된 주문이 있었는데, 상품을 나중에 등록하면요?
          </AccordionTrigger>
          <AccordionContent className="break-keep text-sm leading-relaxed text-slate-500">
            {/* "등록 이후 주문부터"는 오답(codexloop 수용 — 등록 시점의 입금 전 주문도 즉시
                예약 반영). '소급 차감' 같은 행정 어휘 대신 쉬운 표현(오너 2026-07-17). */}
            등록 전에 이미 입금 완료된 주문은 재고에서 빠지지 않습니다.
            <br />
            아직 입금 전인 주문은 등록하는 순간 예약으로 잡히고, 입금 완료되면 차감됩니다.
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="name-matching" className="border-b-0 border-slate-100">
          <AccordionTrigger className="break-keep py-3 text-slate-700 hover:no-underline hover:text-slate-900 [&>svg]:text-slate-400">
            주문 상품명이 조금 달라도 연결되나요?
          </AccordionTrigger>
          <AccordionContent className="break-keep text-sm leading-relaxed text-slate-500">
            {/* 대시(—) 금지(오너 2026-07-17), 문장별 줄바꿈. */}
            공백과 영문 대소문자는 무시합니다.
            <br />
            '반팔 티셔츠'와 '반팔티셔츠'는 같은 상품으로 연결됩니다.
            <br />그 외 표기가 다르면(색상·옵션 등) 다른 상품으로 취급합니다.
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
